import React from 'react';
import { ToolbarComponent } from './toolbar-component';
import { Conversation } from '../api/models/Conversation';
import { useNavigate } from 'react-router-dom';
import { Editor } from '@monaco-editor/react';
import { useAppSelector } from '../store/hooks';
import { useDispatch } from 'react-redux';
import { setNotification } from '../store/notifications-reducer';
import { Artifact } from '../api/models/Artifact';
import { updateArtifact } from '../api/thunks/artifacts';
import { ArrowDownTrayIcon, TrashIcon } from '@heroicons/react/24/solid';

interface ArtifactsPageProps {
    handleUpdateConversation: (id: string, name: string) => void;
    artifacts: Artifact[];
    pagination: {
        page: number;
        pageSize: number;
        total: number;
        totalPages: number;
        hasMore: boolean;
    };
    onPageChange: (page: number) => void;
    onPageSizeChange: (pageSize: number) => void;
    convo: Conversation | undefined;
    convos: Conversation[];
    handleCreateConversation: (ticker: string) => void;
    supportedTickers: string[];
    highlighter: any; // Adjust this type based on the highlighter you're using
    handleDeleteArtifact: (id: string) => void;
    handleDeleteConversation: (id: string) => Promise<void>;
    handleDeleteShareLink: (conversationId: string) => void;
    handleCreateShareLink: (conversationId: string) => void;
}

export const ArtifactsPage: React.FC<ArtifactsPageProps> = ({ handleUpdateConversation, artifacts, pagination, onPageChange, onPageSizeChange, convo, convos, handleCreateConversation, supportedTickers, highlighter, handleDeleteArtifact, handleDeleteConversation, handleDeleteShareLink, handleCreateShareLink }) => {
    document.title = 'Gallery | PocketQuant';
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const credentials = useAppSelector((state) => state.user.credentials);
    const localArtifacts = [...artifacts];

    const totalPages = Math.max(1, pagination.totalPages || 1);

    if (!highlighter || !credentials) return <></>;

    const handleConversationClick = (id: string) => {
        const queryParams = new URLSearchParams(window.location.search);
        queryParams.set('conversationId', id);
    navigate(`/dashboard?${queryParams.toString()}`);
        navigate(0);
    };

    const handleSaveArtifact = async (artifact: Artifact) => {
        const local = localArtifacts.find(a => a.id === artifact.id)?.data || '';
        const accessToken = credentials.accessToken;
        updateArtifact({ ...artifact, data: local }, accessToken).then(() => {
            dispatch(setNotification({ type: 'success', message: 'Artifact saved successfully' }));
        }).catch((error) => {
            dispatch(setNotification({ type: 'error', message: error.message }));
        });
    }

    return (
        <div className='artifact-workspace grid grid-rows-[1fr_auto] h-screen overflow-hidden'>
            {/* Artifacts Grid */}
            <div className="flex-1 overflow-y-auto">
                {artifacts.length === 0 ? (
                    <div className="flex justify-center items-center h-full text-lg text-gray-500">
                        No artifacts available. Start a conversation to generate artifacts.
                    </div>
                ) : (
                    <div className="max-w-screen-xl mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 p-4">
                        {localArtifacts.map((artifact) => (
                            <div
                                key={artifact.id}
                                className="rounded-lg shadow-md p-4 hover:bg-gray-50 transition-all flex flex-col h-full"
                                style={{ minHeight: 0 }}
                            >
                                <div className="flex flex-col justify-between h-full">
                                    <h2 className="text-xl mb-2 font-semibold text-gray-700 flex justify-between items-center">
                                        <span className="truncate flex-1 mr-2" title={artifact.name.replaceAll("\"", "")}>{artifact.name.replaceAll("\"", "")}</span>
                                        <TrashIcon onClick={() => {
                                            handleDeleteArtifact(artifact.id);
                                        }} className='w-6 h-6 cursor-pointer flex-shrink-0' />
                                    </h2>
                                    {artifact.artifactType === 'code' ?
                                        <>
                                            <div className="flex-1 min-h-0">
                                                <Editor
                                                    height={'200px'}
                                                    defaultLanguage='python'
                                                    defaultValue={artifact.data}
                                                    onChange={(value) => {
                                                        const a = localArtifacts.find((a) => a.id === artifact.id)
                                                        if (!a) return;
                                                        a.data = value || '';
                                                    }}
                                                    theme='vs-dark'
                                                />
                                            </div>
                                            <div className="flex items-center justify-between">
                                                <button
                                                    onClick={() => navigator.clipboard.writeText(artifact.data)}
                                                    className="mt-2 px-4 py-2 bg-orange-500 text-white rounded-md text-sm w-full whitespace-nowrap"
                                                >
                                                    Copy Code
                                                </button>
                                                <button onClick={() => handleSaveArtifact(artifact)} className="mt-2 ml-2 px-4 py-2 bg-orange-500 text-white rounded-md text-sm w-full" >
                                                    Save Code
                                                </button>
                                            </div></>
                                        : <div className="flex-1 flex flex-col justify-between relative min-h-0">
                                            <div className="flex-1 flex items-center justify-center min-h-0">
                                                <img
                                                    src={`data:image/png;base64, ${artifact.data}`}
                                                    alt={artifact.name}
                                                    className="object-contain rounded-md p-2 max-h-72 w-auto h-auto max-w-full"
                                                    style={{ maxHeight: '18rem', width: '100%', height: 'auto' }}
                                                />
                                            </div>
                                            <button
                                                className="absolute bottom-4 right-4 bg-black bg-opacity-50 text-white flex items-center justify-center p-2 rounded-full hover:bg-opacity-75 focus:outline-none focus:ring-2 focus:ring-white transition-opacity"
                                                onClick={() => {
                                                    const link = document.createElement("a");
                                                    link.href = `data:image/png;base64, ${artifact.data}`;
                                                    link.download = "image.png"; // Default filename
                                                    link.click();
                                                }}
                                            >
                                                <ArrowDownTrayIcon className="w-5 h-5" />
                                            </button>
                                        </div>
                                    }
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
            <div className="border-t border-gray-200 bg-white px-4 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="text-sm text-gray-600">
                    Showing <span className="font-medium">{artifacts.length}</span> of <span className="font-medium">{pagination.total}</span> artifacts
                </div>
                <div className="flex flex-wrap gap-3 items-center justify-end">
                    <label className="text-sm text-gray-600">
                        Page Size
                        <select
                            value={pagination.pageSize}
                            onChange={(event) => onPageSizeChange(parseInt(event.target.value, 10))}
                            className="ml-2 w-16 border border-gray-300 rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                        >
                            {[6, 12, 18, 24, 30, 36].map((size) => (
                                <option key={size} value={size}>{size}</option>
                            ))}
                        </select>
                    </label>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => onPageChange(Math.max(1, pagination.page - 1))}
                            disabled={pagination.page <= 1}
                            className={`px-3 py-1 rounded-md text-sm border ${pagination.page <= 1 ? 'border-gray-200 text-gray-400 cursor-not-allowed' : 'border-gray-300 text-gray-700 hover:bg-gray-100'}`}
                        >
                            Previous
                        </button>
                        <span className="text-sm text-gray-700">
                            Page <span className="font-medium">{pagination.page}</span> of <span className="font-medium">{totalPages}</span>
                        </span>
                        <button
                            onClick={() => onPageChange(Math.min(totalPages, pagination.page + 1))}
                            disabled={!pagination.hasMore}
                            className={`px-3 py-1 rounded-md text-sm border ${!pagination.hasMore ? 'border-gray-200 text-gray-400 cursor-not-allowed' : 'border-gray-300 text-gray-700 hover:bg-gray-100'}`}
                        >
                            Next
                        </button>
                    </div>
                </div>
            </div>
            <ToolbarComponent
                handleUpdateConversation={handleUpdateConversation}
                convo={convo}
                convos={convos}
                handleConversationClick={handleConversationClick}
                handleCreateConversation={handleCreateConversation}
                supportedTickers={supportedTickers}
                handleDeleteConversation={handleDeleteConversation}
                handleDeleteShareLink={handleDeleteShareLink}
                handleCreateShareLink={handleCreateShareLink}
            />
        </div>
    );
};
